/**
 * Google Tag Manager + Consent Mode v2 bootstrap.
 *
 * - `initGTM(id)` is idempotent: safe to call multiple times, only injects once
 * - Consent defaults to "denied" for analytics_storage + ad_storage until
 *   `updateConsent(true)` is called from the cookie banner
 * - `mbsConsentUpdate` is exposed on window for convenience
 */

const CONSENT_STORAGE_KEY = "mbs_cookie_consent";

let initialized = false;

const ensureDataLayer = () => {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

function gtag() {
  const dl = ensureDataLayer();
  if (!dl) return;
  // GTM/GA4 expects the raw arguments object pushed as a single element.
  // eslint-disable-next-line prefer-rest-params
  dl.push(arguments);
}

const injectScript = (containerId) => {
  const src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  if (document.querySelector(`script[src^="https://www.googletagmanager.com/gtm.js?id="]`)) {
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

const injectNoscript = (containerId) => {
  if (document.getElementById("mbs-gtm-noscript")) return;
  const noscript = document.createElement("noscript");
  noscript.id = "mbs-gtm-noscript";
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(containerId)}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  if (document.body) {
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

const readStoredConsent = () => {
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (v === "accepted") return true;
    if (v === "denied") return false;
    return null;
  } catch (e) {
    return null;
  }
};

const writeStoredConsent = (granted) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, granted ? "accepted" : "denied");
  } catch (e) {
    // ignore (private mode / storage disabled)
  }
};

/**
 * Apply a consent decision to the dataLayer (Consent Mode v2).
 * Persists the decision to localStorage so it survives reloads.
 */
export const updateConsent = (granted) => {
  ensureDataLayer();
  const value = granted ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
  writeStoredConsent(granted);
};

/**
 * Initialize GTM with Consent Mode v2 defaults.
 * Safe to call repeatedly — only the first call has effect.
 *
 * @param {string} containerId - GTM-XXXXXXX
 */
export const initGTM = (containerId) => {
  if (initialized) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!containerId || typeof containerId !== "string") return;

  ensureDataLayer();

  // Consent Mode v2 defaults (must run BEFORE gtm.js loads)
  gtag("consent", "default", {
    ad_storage: "denied",
    analytics_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  // GTM bootstrap timestamp event
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  injectScript(containerId);
  injectNoscript(containerId);

  // If the user already accepted/refused on a previous visit, re-apply silently.
  const stored = readStoredConsent();
  if (stored !== null) {
    updateConsent(stored);
  }

  // Expose helper for non-React callers (cookie banner, etc.)
  window.mbsConsentUpdate = updateConsent;

  initialized = true;
};

/**
 * Returns the persisted consent decision, or null if the user hasn't decided.
 */
export const getStoredConsent = () => readStoredConsent();

export const CONSENT_KEY = CONSENT_STORAGE_KEY;
